import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { lazy, Suspense, useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Fast-critical pages — direct import
import Landing from "@/pages/public/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import PatientDashboard from "@/pages/patient/PatientDashboard";
import DoctorDashboard from "@/pages/doctor/DoctorDashboard";
import DoctorQueue from "@/pages/doctor/DoctorQueue";
import DoctorAppointments from "@/pages/doctor/DoctorAppointments";
import DoctorsList from "@/pages/booking/DoctorsList";
import DoctorProfileBooking from "@/pages/booking/DoctorProfileBooking";
import ApplyAsDoctor from "@/pages/doctor/ApplyAsDoctor";
import ErrorBoundary from "@/components/ErrorBoundary";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";
import SupportFloatingButton from "@/components/SupportFloatingButton";
import BrandedPageLoader from "@/components/BrandedPageLoader";
import { installGlobalErrorReporting } from "@/lib/errorReporting";
import { installChunkLoadRecovery } from "@/lib/chunkRecovery";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import StaffLayout from "@/pages/staff/StaffLayout";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import TermsPage from "@/pages/public/TermsPage";
import PrivacyPolicyPage from "@/pages/public/PrivacyPolicyPage";
import ContactUs from "@/pages/public/ContactUs";
import AboutUs from "@/pages/public/AboutUs";
import CareersPage from "@/pages/public/CareersPage";

// Heavy pages — lazy loaded
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboardHome = lazy(() => import("@/pages/admin/Dashboard"));
const AdminPendingDoctors = lazy(() => import("@/pages/admin/PendingDoctors"));
const AdminApprovedDoctors = lazy(() => import("@/pages/admin/ApprovedDoctors"));
const AdminRejectedDoctors = lazy(() => import("@/pages/admin/RejectedDoctors"));
const AdminAddDoctor = lazy(() => import("@/pages/admin/AddDoctor"));
const AdminProcedures = lazy(() => import("@/pages/admin/Procedures"));
const AdminAppointments = lazy(() => import("@/pages/admin/Appointments"));
const AdminPatients = lazy(() => import("@/pages/admin/Patients"));
const AdminRevenue = lazy(() => import("@/pages/admin/Revenue"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAlerts = lazy(() => import("@/pages/admin/Alerts"));
const AdminAuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const AdminSecurityEvents = lazy(() => import("@/pages/admin/SecurityEvents"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));
const AdminLaunchChecklist = lazy(() => import("@/pages/admin/LaunchChecklist"));
const AdminFeaturedDoctors = lazy(() => import("@/pages/admin/FeaturedDoctors"));
const AdminSpecialtiesManager = lazy(() => import("@/pages/admin/SpecialtiesManager"));
const AdminHomepageContent = lazy(() => import("@/pages/admin/HomepageContent"));
const AdminSupportTickets = lazy(() => import("@/pages/admin/SupportTickets"));
const AdminPlatformHealth = lazy(() => import("@/pages/admin/PlatformHealth"));
const AdminLockedAccounts = lazy(() => import("@/pages/admin/LockedAccounts"));
const AdminOperationalEvents = lazy(() => import("@/pages/admin/OperationalEvents"));
const AdminDataQuality = lazy(() => import("@/pages/admin/DataQuality"));
const AdminMedicalAccessAudit = lazy(() => import("@/pages/admin/MedicalAccessAudit"));
const AdminErasureRequests = lazy(() => import("@/pages/admin/ErasureRequests"));
const AdminComplianceCenter = lazy(() => import("@/pages/admin/ComplianceCenter"));
const DoctorPending = lazy(() => import("@/pages/doctor/DoctorPending"));
const DoctorProfileEdit = lazy(() => import("@/pages/doctor/DoctorProfileEdit"));
const ProcedureBookings = lazy(() => import("@/pages/doctor/ProcedureBookings"));
const ManageProcedures = lazy(() => import("@/pages/doctor/ManageProcedures"));
const PatientProfile = lazy(() => import("@/pages/patient/PatientProfile"));
const FamilyMembers = lazy(() => import("@/pages/patient/FamilyMembers"));
const ReviewsPage = lazy(() => import("@/pages/shared/ReviewsPage"));
const RevenueAnalytics = lazy(() => import("@/pages/doctor/RevenueAnalytics"));
const PlanDetails = lazy(() => import("@/pages/doctor/PlanDetails"));
const TreatmentPlans = lazy(() => import("@/pages/doctor/TreatmentPlans"));
const MyTreatments = lazy(() => import("@/pages/patient/MyTreatments"));
const NotificationsPage = lazy(() => import("@/pages/shared/Notifications"));
const SupportTickets = lazy(() => import("@/pages/shared/SupportTickets"));
const DoctorAccountSettings = lazy(() => import("@/pages/doctor/DoctorAccountSettings"));
const DoctorPatients = lazy(() => import("@/pages/doctor/DoctorPatients"));
const RescheduleCenter = lazy(() => import("@/pages/doctor/RescheduleCenter"));
const FutureAppointments = lazy(() => import("@/pages/doctor/FutureAppointments"));
const StaffDashboard = lazy(() => import("@/pages/staff/StaffDashboard"));
const StaffAppointments = lazy(() => import("@/pages/staff/StaffAppointments"));
const StaffPatients = lazy(() => import("@/pages/staff/StaffPatients"));
const StaffProcedures = lazy(() => import("@/pages/staff/StaffProcedures"));
const StaffTreatmentPlans = lazy(() => import("@/pages/staff/StaffTreatmentPlans"));
const PatientImport = lazy(() => import("@/pages/doctor/PatientImport"));
const MyQueue = lazy(() => import("@/pages/patient/MyQueue"));
const MedicalRecords = lazy(() => import("@/pages/patient/MedicalRecords"));
const MyDataPrivacy = lazy(() => import("@/pages/patient/MyDataPrivacy"));
const QueueDisplay = lazy(() => import("@/pages/shared/QueueDisplay"));

function dashboardFor(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/admin";
  if (user.role === "doctor") return "/doctor";
  if (user.role === "staff") return "/staff";
  return "/dashboard";
}

function PageSkeleton() {
  return <BrandedPageLoader />;
}

function Sus({ children }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

function HomeRedirect({ children }) {
  const { user, loading, warmingUp } = useAuth();
  if (loading && warmingUp) return <PageSkeleton />;
  if (loading) return null;
  if (user && (user.role === "doctor" || user.role === "admin" || user.role === "staff")) {
    return <Navigate to={dashboardFor(user)} replace />;
  }
  return children;
}

function ProtectedRoute({ children, role }) {
  const { user, loading, warmingUp } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <BrandedPageLoader
        message={warmingUp ? "Connecting securely" : "Loading"}
        subtext={warmingUp ? "Checking your session" : "Preparing your workspace"}
      />
    );
  }
  if (!user) {
    const next = `${location.pathname}${location.search || ""}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (role && user.role !== role) {
    return <Navigate to={dashboardFor(user)} replace />;
  }
  return children;
}

function RequireAdminPerm({ permission, children }) {
  const { user } = useAuth();
  if (!permission || user?.admin_role === "super_admin" || (user?.admin_permissions || []).includes(permission)) {
    return children;
  }
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive text-xl font-bold">!</div>
        <h2 className="font-display font-semibold text-lg text-foreground">Access Restricted</h2>
        <p className="text-sm text-muted-foreground mt-1">You don&apos;t have permission to view this page.</p>
      </div>
    </div>
  );
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={dashboardFor(user)} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    installGlobalErrorReporting();
    installChunkLoadRecovery();
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: "#224032",
                color: "#FDFBF7",
                border: "1px solid #1a3025",
                fontFamily: "Manrope, sans-serif",
              },
            }}
          />
          <MaintenanceOverlay>
          <Routes>
            <Route path="/" element={<HomeRedirect><Landing /></HomeRedirect>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/apply" element={<ApplyAsDoctor />} />
            <Route path="/doctors" element={<DoctorsList />} />
            <Route path="/doctors/:id" element={<DoctorProfileBooking />} />
            <Route path="/doctors/:doctorId/reviews" element={<Sus><ReviewsPage /></Sus>} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute role="patient">
                  <Sus><PatientProfile /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medical-records"
              element={
                <ProtectedRoute role="patient">
                  <Sus><MedicalRecords /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-data"
              element={
                <ProtectedRoute role="patient">
                  <Sus><MyDataPrivacy /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Sus><NotificationsPage /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <Sus><SupportTickets /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute role="patient">
                  <PatientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/family"
              element={
                <ProtectedRoute role="patient">
                  <Sus><FamilyMembers /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-treatments"
              element={
                <ProtectedRoute role="patient">
                  <Sus><MyTreatments /></Sus>
                </ProtectedRoute>
              }
            />
            <Route element={<ProtectedRoute role="staff"><StaffLayout /></ProtectedRoute>}>
              <Route path="/staff" element={<Sus><StaffDashboard /></Sus>} />
              <Route path="/staff/appointments" element={<Sus><StaffAppointments /></Sus>} />
              <Route path="/staff/future-appointments" element={<Sus><FutureAppointments /></Sus>} />
              <Route path="/staff/treatment-plans" element={<Sus><StaffTreatmentPlans /></Sus>} />
              <Route path="/staff/patients" element={<Sus><StaffPatients /></Sus>} />
              <Route path="/staff/procedures" element={<Sus><StaffProcedures /></Sus>} />
              <Route path="/staff/import-patients" element={<Sus><PatientImport /></Sus>} />
              <Route path="/staff/reschedule-center" element={<Sus><RescheduleCenter /></Sus>} />
            </Route>
            <Route element={<ProtectedRoute role="doctor"><DoctorLayout /></ProtectedRoute>}>
              <Route path="/doctor" element={<DoctorDashboard />} />
              <Route path="/doctor/queue" element={<DoctorQueue />} />
              <Route path="/doctor/appointments" element={<DoctorAppointments />} />
              <Route path="/doctor/future-appointments" element={<Sus><FutureAppointments /></Sus>} />
              <Route path="/doctor/profile" element={<Sus><DoctorProfileEdit /></Sus>} />
              <Route path="/doctor/procedure-bookings" element={<Sus><ProcedureBookings /></Sus>} />
              <Route path="/doctor/manage-procedures" element={<Sus><ManageProcedures /></Sus>} />
              <Route path="/doctor/treatment-plans" element={<Sus><TreatmentPlans /></Sus>} />
              <Route path="/doctor/patients" element={<Sus><DoctorPatients /></Sus>} />
              <Route path="/doctor/patients/:profileKey" element={<Sus><DoctorPatients /></Sus>} />
              <Route path="/doctor/import-patients" element={<Sus><PatientImport /></Sus>} />
              <Route path="/doctor/reschedule-center" element={<Sus><RescheduleCenter /></Sus>} />
              <Route path="/plan-details" element={<Sus><PlanDetails /></Sus>} />
              <Route path="/analytics/revenue" element={<Sus><RevenueAnalytics /></Sus>} />
            </Route>
            <Route
              path="/doctor/account-settings"
              element={
                <ProtectedRoute role="doctor">
                  <Sus><DoctorAccountSettings /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/doctor/queue-display"
              element={
                <ProtectedRoute role="doctor">
                  <Sus><QueueDisplay /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff/queue-display"
              element={
                <ProtectedRoute role="staff">
                  <Sus><QueueDisplay /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/doctor-pending"
              element={
                <ProtectedRoute role="doctor">
                  <Sus><DoctorPending /></Sus>
                </ProtectedRoute>
              }
            />
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Sus><RequireAdminPerm permission="admin.dashboard.view"><AdminDashboardHome /></RequireAdminPerm></Sus>} />
              <Route path="pending" element={<Sus><RequireAdminPerm permission="admin.doctors.view"><AdminPendingDoctors /></RequireAdminPerm></Sus>} />
              <Route path="approved" element={<Sus><RequireAdminPerm permission="admin.doctors.view"><AdminApprovedDoctors /></RequireAdminPerm></Sus>} />
              <Route path="rejected" element={<Sus><RequireAdminPerm permission="admin.doctors.view"><AdminRejectedDoctors /></RequireAdminPerm></Sus>} />
              <Route path="doctors/new" element={<Sus><RequireAdminPerm permission="admin.doctors.edit"><AdminAddDoctor /></RequireAdminPerm></Sus>} />
              <Route path="featured" element={<Sus><RequireAdminPerm permission="admin.featured_doctors.manage"><AdminFeaturedDoctors /></RequireAdminPerm></Sus>} />
              <Route path="specialties" element={<Sus><RequireAdminPerm permission="admin.specialties.manage"><AdminSpecialtiesManager /></RequireAdminPerm></Sus>} />
              <Route path="homepage-content" element={<Sus><RequireAdminPerm permission="admin.content.manage"><AdminHomepageContent /></RequireAdminPerm></Sus>} />
              <Route path="procedures" element={<Sus><RequireAdminPerm permission="admin.procedures.manage"><AdminProcedures /></RequireAdminPerm></Sus>} />
              <Route path="appointments" element={<Sus><RequireAdminPerm permission="admin.appointments.view"><AdminAppointments /></RequireAdminPerm></Sus>} />
              <Route path="patients" element={<Sus><RequireAdminPerm permission="admin.patients.view"><AdminPatients /></RequireAdminPerm></Sus>} />
              <Route path="revenue" element={<Sus><RequireAdminPerm permission="admin.revenue.view"><AdminRevenue /></RequireAdminPerm></Sus>} />
              <Route path="alerts" element={<Sus><RequireAdminPerm permission="admin.alerts.view"><AdminAlerts /></RequireAdminPerm></Sus>} />
              <Route path="reports" element={<Sus><RequireAdminPerm permission="admin.reports.view"><AdminReports /></RequireAdminPerm></Sus>} />
              <Route path="audit" element={<Sus><RequireAdminPerm permission="admin.audit.view"><AdminAuditLog /></RequireAdminPerm></Sus>} />
              <Route path="security-events" element={<Sus><RequireAdminPerm permission="admin.audit.view"><AdminSecurityEvents /></RequireAdminPerm></Sus>} />
              <Route path="medical-access-audit" element={<Sus><RequireAdminPerm permission="admin.audit.view"><AdminMedicalAccessAudit /></RequireAdminPerm></Sus>} />
              <Route path="compliance" element={<Sus><RequireAdminPerm permission="admin.compliance.view"><AdminComplianceCenter /></RequireAdminPerm></Sus>} />
              <Route path="erasure-requests" element={<Sus><RequireAdminPerm permission="admin.compliance.view"><AdminErasureRequests /></RequireAdminPerm></Sus>} />
              <Route path="notifications" element={<Sus><RequireAdminPerm permission="admin.notifications.send"><AdminNotifications /></RequireAdminPerm></Sus>} />
              <Route path="launch" element={<Sus><RequireAdminPerm permission="admin.launch.checklist"><AdminLaunchChecklist /></RequireAdminPerm></Sus>} />
              <Route path="settings" element={<Sus><RequireAdminPerm permission="admin.settings.manage"><AdminSettings /></RequireAdminPerm></Sus>} />
              <Route path="users" element={<Sus><RequireAdminPerm permission="admin.users.manage"><AdminUsers /></RequireAdminPerm></Sus>} />
              <Route path="support" element={<Sus><RequireAdminPerm permission="admin.support.view"><AdminSupportTickets /></RequireAdminPerm></Sus>} />
              <Route path="platform-health" element={<Sus><RequireAdminPerm permission="admin.system_health.view"><AdminPlatformHealth /></RequireAdminPerm></Sus>} />
              <Route path="operational-events" element={<Sus><RequireAdminPerm permission="admin.operational_events.view"><AdminOperationalEvents /></RequireAdminPerm></Sus>} />
              <Route path="data-quality" element={<Sus><RequireAdminPerm permission="admin.operational_events.view"><AdminDataQuality /></RequireAdminPerm></Sus>} />
              <Route path="security" element={<Sus><RequireAdminPerm permission="admin.security.view"><AdminLockedAccounts /></RequireAdminPerm></Sus>} />
            </Route>
            <Route path="/me" element={<RoleRedirect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route
              path="/my-queue"
              element={
                <ProtectedRoute role="patient">
                  <Sus><MyQueue /></Sus>
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-queue"
              element={
                <ProtectedRoute role="patient">
                  <Sus><MyQueue /></Sus>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <SupportFloatingButton />
          </MaintenanceOverlay>
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
